import {
  createContext,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  User as FirebaseUser,
  onIdTokenChanged,
  signOut,
  signInWithEmailAndPassword,
  UserCredential,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
} from 'firebase/auth';
import { firebaseAuth, firestore } from '../config/firebaseClient';
import { setCookie } from 'cookies-next';
import { getOnlineStatus } from '../util/realtime/getOnlineStatus';
import { useDisconnect } from 'wagmi';
import { User } from 'functions/src/types/firestore/User';
import { doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { ConverterFactory } from 'functions/src/util/firestore/ConverterFactory';
import { isUserVerified } from '../util/profile/isUserVerified';
// import { useGlobalComponentsContext } from './GlobalComponentsContext';
// import { Typography, Button } from '@mui/material';
// import { DialogCentered } from 'src/components/DialogCentered';

const TOKEN_REFETCH_INTERVAL_MILLIS = 10 * 60 * 1000;

type OwnedItemMap = { [contractAddress: string]: number[] };

export type AuthContextType = {
  user: FirebaseUser | null;
  userData: User<Date> | null;
  handleSignOut: () => Promise<void>;
  handleSignInWithEmailAndPassword: (
    email: string,
    password: string,
  ) => Promise<UserCredential>;
  handleEmailCheck: (email: string) => Promise<string[]>;
  handleCreateAccount: (
    email: string,
    password: string,
  ) => Promise<UserCredential>;
  isUnverified: boolean;
  isLoading: boolean;
  ownedItems: OwnedItemMap | null;
  checkItemOwnership: (contractAddress: string, itemId: number) => boolean;
};

const AuthContext = createContext<AuthContextType>({
  user: null,
  userData: null,
  handleSignOut: async () => {
    /* */
  },
  handleSignInWithEmailAndPassword: async (email: string, password: string) => {
    return signInWithEmailAndPassword(firebaseAuth, email, password);
  },
  handleEmailCheck: async (email: string) => {
    return fetchSignInMethodsForEmail(firebaseAuth, email);
  },
  handleCreateAccount: async (email: string, password: string) => {
    return createUserWithEmailAndPassword(firebaseAuth, email, password);
  },
  isUnverified: false,
  isLoading: true,
  ownedItems: {},
  checkItemOwnership: () => {
    return false;
  },
});

type AuthProviderProps = {
  children: ReactElement;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [ownedItems, setOwnedItems] = useState<OwnedItemMap | null>(null);
  const [user, setUser] = useState<FirebaseUser | null | undefined>(undefined);
  const [userData, setUserData] = useState<User<Date> | null>(null);
  const [isUnverified, setIsUniverified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  let unsubscribeIdTokenChanged: Unsubscribe | null = null;
  let unsubscribeSnapshot: Unsubscribe | null = null;

  const handleSignOut = () => {
    if (unsubscribeSnapshot) {
      unsubscribeSnapshot();
      unsubscribeSnapshot = null;
    }
    return signOut(firebaseAuth);
  };

  const { disconnectAsync } = useDisconnect();
  useEffect(() => {
    if (!onIdTokenChanged) {
      return;
    }

    const handler = async () => {
      try {
        unsubscribeIdTokenChanged = onIdTokenChanged(
          firebaseAuth,
          async (firebaseUser) => {
            setIsLoading(false);
            if (!firebaseUser) {
              setUser(null);
              setUserData(null);
              disconnectAsync();
              setCookie('firebase_auth_token', '', { path: '/' });
            } else {
              const token = await firebaseUser.getIdToken();
              setUser(firebaseUser);
              setCookie('firebase_auth_token', token, { path: '/' });
              getOnlineStatus();
              if (unsubscribeSnapshot) {
                unsubscribeSnapshot();
                unsubscribeSnapshot = null;
              }
              unsubscribeSnapshot = onSnapshot(
                doc(firestore, `User/${firebaseUser.uid}`).withConverter(
                  ConverterFactory.buildDateConverter<User<Date>>(),
                ),
                (updatedDoc) => {
                  if (
                    updatedDoc.exists() &&
                    updatedDoc.data() &&
                    !!updatedDoc.data().id
                  ) {
                    setUserData(updatedDoc.data());
                  }
                },
              );
            }
          },
        );
      } catch (error) {
        console.error(error);
      }
    };
    handler();
    return () => {
      if (unsubscribeSnapshot) {
        unsubscribeSnapshot();
      }
      if (unsubscribeIdTokenChanged) {
        unsubscribeIdTokenChanged();
      }
    };
  }, [disconnectAsync]);

  useEffect(() => {
    setIsUniverified(!isUserVerified(user || null, userData));
  }, [user, userData]);

  useEffect(() => {
    const handler = setInterval(async () => {
      const firebaseUser = firebaseAuth.currentUser;
      if (firebaseUser) {
        await firebaseUser.getIdToken(true);
      }
    }, TOKEN_REFETCH_INTERVAL_MILLIS);

    return () => {
      return clearInterval(handler);
    };
  }, []);

  useEffect(() => {
    if (!userData?.addresses) {
      return;
    }
    const handler = async () => {
      // TODO: Disabling as we're hitting Moralis rate limits
      // const addresses = userData.addresses as string[];
      // const owned = await Promise.all(
      //   addresses.map(async (address: string) => {
      //     const res = await getAllOwned({
      //       verbose: false,
      //       walletAddress: address,
      //     });
      //     const ownedConcise = res.data as {
      //       contractAddress: string;
      //       itemId: number;
      //     }[];
      //     return ownedConcise;
      //   }),
      // );
      // if (!owned.length) {
      //   return;
      // }
      // const grouped: { [key: string]: number[] } = owned
      //   .flat()
      //   .reduce((acc, curr) => {
      //     if (!acc[curr.contractAddress]) {
      //       acc[curr.contractAddress] = [curr.itemId];
      //     } else {
      //       acc[curr.contractAddress].push(curr.itemId);
      //     }
      //     return acc;
      //   }, {});
      setOwnedItems({});
    };
    handler();
  }, [userData?.addresses]);

  return (
    <AuthContext.Provider
      value={{
        user: user || null,
        userData,
        handleSignOut,
        handleSignInWithEmailAndPassword: (email: string, password: string) => {
          return signInWithEmailAndPassword(firebaseAuth, email, password);
        },
        handleEmailCheck: (email: string) => {
          return fetchSignInMethodsForEmail(firebaseAuth, email);
        },
        handleCreateAccount: (email: string, password: string) => {
          return createUserWithEmailAndPassword(firebaseAuth, email, password);
        },
        isUnverified,
        isLoading,
        ownedItems,
        checkItemOwnership: (contractAddress: string, itemId: number) => {
          if (!ownedItems) {
            return false;
          }
          return ownedItems[`${contractAddress}`]?.includes(itemId);
        },
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
